import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@vinoga/db";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useT } from "@/lib/i18n";

const PAGE_SIZE = 50;

export function Customers() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const t = useT();
  const [customers, setCustomers] = useState<any[]>([]);
  const [groups, setGroups] = useState<
    Record<string, { code: string; name: string; is_business: boolean }>
  >({});
  const [groupOrder, setGroupOrder] = useState<string[]>([]);
  const [b2cGroupId, setB2cGroupId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterGroup, setFilterGroup] = useState<"all" | "b2b" | "b2c" | string>("all");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("customer_groups")
        .select("id, code, name, is_business")
        .order("code");
      const map: Record<string, { code: string; name: string; is_business: boolean }> = {};
      const order: string[] = [];
      let b2c: string | null = null;
      (data ?? []).forEach((g: any) => {
        map[g.id] = { code: g.code, name: g.name, is_business: g.is_business };
        order.push(g.id);
        if (g.code === "b2c") b2c = g.id;
      });
      setGroups(map);
      setGroupOrder(order);
      setB2cGroupId(b2c);
    })();
  }, []);

  const fetchData = useCallback(
    async (resetPage = false) => {
      setLoading(true);
      const currentPage = resetPage ? 0 : page;
      const from = currentPage * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const dbSortable = ["created_at", "first_name", "last_name", "email", "last_login_at"];
      let query = supabase
        .from("customers")
        .select("*", { count: "exact" })
        .order(dbSortable.includes(sortColumn) ? sortColumn : "created_at", {
          ascending: dbSortable.includes(sortColumn) ? sortDirection === "asc" : false,
        });

      if (search.trim()) {
        const s = search.trim();
        query = query.or(
          `first_name.ilike.%${s}%,last_name.ilike.%${s}%,email.ilike.%${s}%,phone.ilike.%${s}%`
        );
      }

      if (filterGroup === "b2b") {
        const ids = Object.entries(groups)
          .filter(([, g]) => g.is_business)
          .map(([id]) => id);
        if (ids.length) query = query.in("customer_group_id", ids);
      } else if (filterGroup === "b2c") {
        const ids = Object.entries(groups)
          .filter(([, g]) => !g.is_business)
          .map(([id]) => id);
        if (ids.length) query = query.in("customer_group_id", ids);
      } else if (filterGroup !== "all") {
        query = query.eq("customer_group_id", filterGroup);
      }

      query = query.range(from, to);

      const { data: rows, count, error } = await query;

      if (error) {
        toast({
          title: t("customers.errorLoading", "Error loading customers"),
          description: error.message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      setCustomers(rows ?? []);
      setTotalCount(count ?? 0);
      if (resetPage) setPage(0);
      setLoading(false);
    },
    [page, search, sortColumn, sortDirection, filterGroup, groups, toast, t]
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortColumn, sortDirection, filterGroup]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(true);
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setPage(0);
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column)
      return <ArrowUpDown className="ml-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/30" />;
    return sortDirection === "asc" ? (
      <ChevronUp className="ml-1 h-3.5 w-3.5 shrink-0" />
    ) : (
      <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0" />
    );
  };

  const formatLastLogin = (date: string | null) => {
    if (!date) return "—";
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return t("customers.todayLabel", "Today");
    if (diffDays === 1) return t("customers.yesterdayLabel", "Yesterday");
    if (diffDays < 30) return `${diffDays}${t("customers.daysAgoSuffix", "d ago")}`;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  };

  // Create customer dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [createSaving, setCreateSaving] = useState(false);

  const createCustomer = async () => {
    if (!newFirstName.trim() || !newEmail.trim()) return;
    if (!b2cGroupId) {
      toast({
        title: t("customers.groupMissing", "Default group missing"),
        description: t(
          "customers.groupMissingDesc",
          "Could not find b2c customer group. Migrations may not be seeded."
        ),
        variant: "destructive",
      });
      return;
    }
    setCreateSaving(true);
    const { error } = await supabase.from("customers").insert({
      email: newEmail.trim().toLowerCase(),
      first_name: newFirstName.trim() || null,
      last_name: newLastName.trim() || null,
      phone: newPhone.trim() || null,
      customer_group_id: b2cGroupId,
    });
    if (error) {
      toast({
        title: t("customers.errorCreating", "Error creating customer"),
        description: error.message,
        variant: "destructive",
      });
      setCreateSaving(false);
      return;
    }
    toast({
      title: t("common.create", "Created"),
      description: t("customers.created", "Customer created successfully."),
    });
    setCreateOpen(false);
    setNewFirstName("");
    setNewLastName("");
    setNewEmail("");
    setNewPhone("");
    setCreateSaving(false);
    fetchData(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-xl font-bold text-foreground">
          {t("customers.title", "Customers")}{" "}
          {totalCount > 0 && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({totalCount.toLocaleString()})
            </span>
          )}
        </h1>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus size={16} /> {t("customers.newCustomer", "New Customer")}
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            placeholder={t("customers.searchPlaceholder", "Search customers...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={filterGroup} onValueChange={(v) => { setFilterGroup(v); setPage(0); }}>
          <SelectTrigger className={cn("h-9 w-[220px]", filterGroup !== "all" && "border-primary")}>
            <SelectValue placeholder={t("customers.filterAll", "All customers")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("customers.filterAll", "All customers")}</SelectItem>
            <SelectItem value="b2c">{t("customers.filterB2C", "Privātpersonas (B2C)")}</SelectItem>
            <SelectItem value="b2b">{t("customers.filterB2B", "Uzņēmumi (B2B)")}</SelectItem>
            {groupOrder.map((id) => (
              <SelectItem key={id} value={id}>
                {groups[id]?.name ?? id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="p-4 font-body text-sm text-muted-foreground">
          {t("common.loading", "Loading...")}
        </div>
      ) : customers.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground">
          {t("customers.empty", "No customers found.")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead
                  className="cursor-pointer select-none font-bold"
                  onClick={() => handleSort("first_name")}
                >
                  <span className="flex items-center">
                    {t("customers.colFirstName", "First Name")}
                    <SortIcon column="first_name" />
                  </span>
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none font-bold"
                  onClick={() => handleSort("last_name")}
                >
                  <span className="flex items-center">
                    {t("customers.colLastName", "Last Name")}
                    <SortIcon column="last_name" />
                  </span>
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none font-bold"
                  onClick={() => handleSort("email")}
                >
                  <span className="flex items-center">
                    {t("customers.colEmail", "Email")}
                    <SortIcon column="email" />
                  </span>
                </TableHead>
                <TableHead className="font-bold">
                  {t("customers.colPhone", "Phone")}
                </TableHead>
                <TableHead className="font-bold">
                  {t("customers.colGroup", "Group")}
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none font-bold"
                  onClick={() => handleSort("last_login_at")}
                >
                  <span className="flex items-center">
                    {t("customers.colLastLogin", "Last Login")}
                    <SortIcon column="last_login_at" />
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((c) => {
                const group = groups[c.customer_group_id];
                return (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/customers/${c.id}`)}
                  >
                    <TableCell className="py-2.5 font-medium text-foreground">
                      {c.first_name ?? "—"}
                    </TableCell>
                    <TableCell className="py-2.5 font-medium text-foreground">
                      {c.last_name ?? "—"}
                    </TableCell>
                    <TableCell className="py-2.5 text-muted-foreground">
                      {c.email ?? "—"}
                    </TableCell>
                    <TableCell className="py-2.5 text-muted-foreground">
                      {c.phone ?? "—"}
                    </TableCell>
                    <TableCell className="py-2.5">
                      {group ? (
                        <Badge variant={group.code === "b2c" ? "secondary" : "info"}>
                          {group.name}
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="py-2.5 text-muted-foreground">
                      {formatLastLogin(c.last_login_at)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-1">
          <span className="font-body text-xs text-muted-foreground">
            {t("common.showing", "Showing")} {page * PAGE_SIZE + 1}&ndash;
            {Math.min((page + 1) * PAGE_SIZE, totalCount)} {t("common.of", "of")}{" "}
            {totalCount.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2"
              disabled={page === 0}
              onClick={() => setPage(0)}
            >
              «
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft size={14} />
            </Button>
            <span className="px-2 font-body text-xs text-muted-foreground">
              {t("common.page", "Page")} {page + 1} {t("common.of", "of")} {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRightIcon size={14} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(totalPages - 1)}
            >
              »
            </Button>
          </div>
        </div>
      )}

      {/* Create Customer Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("customers.newCustomer", "New Customer")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="font-body text-xs text-muted-foreground">
                  {t("customers.dialogFirstName", "First Name")} *
                </label>
                <Input
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                />
              </div>
              <div>
                <label className="font-body text-xs text-muted-foreground">
                  {t("customers.dialogLastName", "Last Name")}
                </label>
                <Input value={newLastName} onChange={(e) => setNewLastName(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="font-body text-xs text-muted-foreground">
                {t("customers.dialogEmail", "Email")} *
              </label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="font-body text-xs text-muted-foreground">
                {t("customers.dialogPhone", "Phone")}
              </label>
              <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={createCustomer}
              disabled={createSaving || !newFirstName.trim() || !newEmail.trim()}
            >
              {createSaving
                ? t("common.creating", "Creating...")
                : t("common.create", "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
